import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import { FiPlus, FiMinus } from 'react-icons/fi';

const faqItems = [
    {
        question: 'What types of properties does Arcdom build?',
        answer: 'Arcdom specializes in residential properties, primarily developing Duplex Houses, 2-Room Apartments, 3-Room Apartments, and exclusive Penthouse Apartments. We also undertake larger Residential Complex projects integrating various unit types and amenities.'
    },
    {
        question: 'Where are Arcdom projects typically located?',
        answer: 'Our primary focus areas are Florești and various neighborhoods within Cluj-Napoca, including Someșeni and areas near the North Bypass (Donath Park). We select locations offering good accessibility and community potential.'
    },
    {
        question: 'What makes Arcdom\'s construction quality stand out?',
        answer: 'Quality is paramount. We differentiate ourselves by using our own dedicated employees for key stages, allowing us to closely monitor execution. We emphasize quality materials, modern techniques like underfloor heating and triple-glazed windows where specified, and strong relationships with trusted subcontractors.'
    },
    {
        question: 'What does "semi-finished" mean for Arcdom apartments?',
        answer: 'Typically, our semi-finished apartments include completed structural work, plastered walls (usually two coats), installed windows and entrance doors, underfloor heating systems, and connections for utilities. This allows buyers the flexibility to choose their own final finishes like flooring, paint, bathroom fixtures, and interior doors.'
    },
    {
        question: 'How long has Arcdom been in the construction business?',
        answer: 'Arcdom began its journey in 2007, initially focusing on finishing and insulation work. We leveraged that experience to become property developers ourselves in 2015 and have since completed nearly 300 housing units across numerous locations.'
    },
    // Keeping the general timeline question as specific project times vary greatly
    {
        question: 'How long does a typical project take to complete?',
        answer: 'Project timelines vary depending on the scale and complexity. Smaller projects like duplexes might take around 12-18 months, while larger apartment complexes can range from 18-36 months from start to final handover or land registration.'
    },
    // Keeping financing as standard practice
    {
        question: 'How can I finance a property purchase with Arcdom?',
        answer: 'We recommend discussing financing options with your preferred bank. Many financial institutions offer mortgage solutions suitable for new constructions. Our sales team can provide necessary documentation about the property for your bank.'
    },
    // Updated sustainability based on known features
    {
        question: 'What energy efficiency features do your buildings include?',
        answer: 'We prioritize energy efficiency through features like high-quality thermal insulation (a focus since our early days), underfloor heating systems for efficient heat distribution, and often utilize triple-glazed windows with modern PVC joinery to minimize heat loss.'
    },
    // Added question about post-purchase support based on their story
    {
        question: 'What kind of support do you offer after I purchase a property?',
        answer: 'We believe in building lasting relationships and take pride in our client care, which extends beyond the sale. We aim to address post-purchase needs promptly and rely heavily on the satisfaction and recommendations of our clients.'
    }
];
const FAQItem = ({ item, index, isOpen, toggleOpen }) => {
    const [ref, inView] = useInView({
        triggerOnce: true,
        threshold: 0.1,
    });

    return (
        <motion.div
            ref={ref}
            initial={{ opacity: 0, y: 20 }}
            animate={inView ? { opacity: 1, y: 0 } : { opacity: 0, y: 20 }}
            transition={{ duration: 0.4, delay: index * 0.1 }}
            className="border-b border-gray-200 last:border-0"
        >
            <button
                onClick={toggleOpen}
                className="flex justify-between items-center w-full py-6 text-left focus:outline-none"
            >
                <h3 className="text-lg font-medium text-arcdom-navy pr-8">{item.question}</h3>
                <div className={`text-arcdom-gold transition-transform duration-300 ${isOpen ? 'transform rotate-180' : ''}`}>
                    {isOpen ? <FiMinus size={20} /> : <FiPlus size={20} />}
                </div>
            </button>

            <AnimatePresence>
                {isOpen && (
                    <motion.div
                        initial={{ height: 0, opacity: 0 }}
                        animate={{ height: 'auto', opacity: 1 }}
                        exit={{ height: 0, opacity: 0 }}
                        transition={{ duration: 0.3 }}
                        className="overflow-hidden"
                    >
                        <p className="pb-6 text-gray-600">{item.answer}</p>
                    </motion.div>
                )}
            </AnimatePresence>
        </motion.div>
    );
};

const FAQ = () => {
    const [openIndex, setOpenIndex] = useState(0);
    const [ref, inView] = useInView({
        triggerOnce: true,
        threshold: 0.1,
    });

    const toggleFAQ = (index) => {
        setOpenIndex(openIndex === index ? null : index);
    };

    return (
        <section id="faq" className="py-20">
            <div className="container mx-auto px-4">
                <motion.div
                    ref={ref}
                    initial={{ opacity: 0, y: 20 }}
                    animate={inView ? { opacity: 1, y: 0 } : { opacity: 0, y: 20 }}
                    transition={{ duration: 0.6 }}
                    className="text-center mb-16"
                >
                    <h2 className="text-4xl font-bold text-arcdom-navy mb-4">Frequently Asked Questions</h2>
                    <div className="w-20 h-1 bg-arcdom-gold mx-auto mb-6"></div>
                    <p className="max-w-2xl mx-auto text-gray-600">
                        Find answers to common questions about our properties, construction process, and services.
                    </p>
                </motion.div>

                <div className="max-w-3xl mx-auto">
                    {faqItems.map((item, index) => (
                        <FAQItem
                            key={index}
                            item={item}
                            index={index}
                            isOpen={openIndex === index}
                            toggleOpen={() => toggleFAQ(index)}
                        />
                    ))}
                </div>

                <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={inView ? { opacity: 1, y: 0 } : { opacity: 0, y: 20 }}
                    transition={{ duration: 0.6, delay: 0.6 }}
                    className="text-center mt-12"
                >
                    <p className="text-gray-600 mb-6">
                        Don't see your question answered? We're here to help!
                    </p>
                    <a
                        href="#contact"
                        className="inline-flex items-center px-6 py-3 bg-arcdom-gold text-arcdom-navy rounded-md font-medium hover:bg-opacity-90 transition-all duration-300"
                    >
                        Ask Us a Question
                        <svg className="w-4 h-4 ml-2" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                            <path d="M5 12H19M19 12L12 5M19 12L12 19" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                        </svg>
                    </a>
                </motion.div>
            </div>
        </section>
    );
};

export default FAQ;
